import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { abcAnalysis } from "@/lib/slotting.functions";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export const Route = createFileRoute("/_authenticated/app/slotting/abc")({ component: AbcPage });

const RANGES = [7, 30, 90, 180] as const;

function bandColor(b: "A" | "B" | "C") {
  return b === "A" ? "default" : b === "B" ? "secondary" : "outline";
}

function AbcPage() {
  const { tenantId } = useScope();
  const fn = useServerFn(abcAnalysis);
  const [days, setDays] = useState<number>(30);

  const { data, isLoading } = useQuery({
    queryKey: ["abc", tenantId, days],
    enabled: !!tenantId,
    queryFn: () => fn({ data: { tenantId: tenantId!, days } }),
    placeholderData: keepPreviousData,
  });

  const counts = data?.counts ?? { A: 0, B: 0, C: 0 };
  const share = data?.unitShare ?? { A: 0, B: 0, C: 0 };
  const total = data?.totalUnits ?? 0;
  const rows = data?.rows ?? [];

  return (
    <>
      <PageHeader title="ABC Velocity Analysis" description="Pareto classification of SKUs by units picked. A=top 80%, B=next 15%, C=remaining." />
      <div className="px-4 sm:px-6 py-4 space-y-4">
        <Tabs value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <TabsList>
            {RANGES.map((d) => <TabsTrigger key={d} value={String(d)}>{d}d</TabsTrigger>)}
          </TabsList>
        </Tabs>

        <div className="grid grid-cols-3 gap-3">
          {(["A", "B", "C"] as const).map((b) => (
            <Card key={b}>
              <CardContent className="p-3">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Band {b}</span>
                  <Badge variant={bandColor(b)}>{counts[b]} SKUs</Badge>
                </div>
                <div className="text-2xl font-semibold tabular-nums mt-1">{total ? Math.round((share[b] / total) * 100) : 0}%</div>
                <div className="text-[11px] text-muted-foreground">{share[b].toLocaleString()} units</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">#</TableHead>
                  <TableHead>SKU</TableHead>
                  <TableHead className="text-right">Units</TableHead>
                  <TableHead className="text-right">Lines</TableHead>
                  <TableHead className="text-right">%</TableHead>
                  <TableHead className="text-right">Cum %</TableHead>
                  <TableHead className="text-center">Band</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>}
                {!isLoading && rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No pick activity in window.</TableCell></TableRow>}
                {rows.slice(0, 500).map((r) => (
                  <TableRow key={r.sku_id}>
                    <TableCell className="text-muted-foreground tabular-nums">{r.rank}</TableCell>
                    <TableCell className="text-xs">
                      <div className="font-mono">{r.sku_code}</div>
                      <div className="text-muted-foreground">{r.name}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{r.units.toLocaleString()}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.lines}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.pct}%</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{r.cum_pct}%</TableCell>
                    <TableCell className="text-center"><Badge variant={bandColor(r.band)}>{r.band}</Badge></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}